import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText } from 'bernie-l10n';
import { mergeQueryString, QueryRoute, updateSearch } from 'bernie-core';

import { IClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { SortAndFiltersViewType } from '@shared-ui/search-tools-lodging';
import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { changeView, View, ViewTracker } from 'src/common/stores/contexts/viewTracker';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { SearchResultsStore } from 'stores/search-results-store';

import { UitkCard, UitkCardContentSection, UitkCardLink } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkLink } from 'uitk-react-link';
import { UitkSecondaryButton } from 'uitk-react-button';

import { forceFetchProperties, handleMapTrigger } from './map-utils';

import { SearchResultsMap } from 'components/search/map';

const LinkTracked = ClickTracker(Link);
const ButtonTracked = ClickTracker(UitkSecondaryButton);

export interface MapPreviewState {
  isActive: boolean;
}

export interface MapPreviewProps extends LocalizedComponentProps, Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  className?: string;
  searchResults?: SearchResultsStore;
  mapImage?: string;
  multiItem?: MultiItemStore;
  tracker: IClickstreamAnalytics;
  viewTracker: ViewTracker;
  dialogView?: SortAndFiltersViewType;
}

@withRouter
@inject('searchResults', 'analytics', 'multiItem')
@observer
export class MapPreview extends React.Component<MapPreviewProps, MapPreviewState> {
  private mapBtn;

  constructor(props) {
    super(props);
    this.state = {
      isActive: false,
    };

    this.onMapDialogClosed = this.onMapDialogClosed.bind(this);
    this.mapBtn = React.createRef();
  }

  public componentDidMount(): void {
    this.setState({ isActive: true });
  }

  public render() {
    const { searchResults, mapImage, analytics, history, location, dialogView } = this.props;
    const { isActive } = this.state;

    if (!searchResults) return null;
    const { isLoading, isError, isEmpty } = searchResults;

    const map = searchResults && searchResults.map;
    const mapOpenQuery = mergeQueryString(location?.search || '', { pwaOverlay: 'map' });
    const linkTo = {
      pathname: location?.pathname,
      search: mapOpenQuery,
    };
    const extraProps =
      isActive && map && Object.keys(map).length > 0
        ? {
            tag: Link,
            to: linkTo,
            role: 'button',
          }
        : {};

    const triggerRef = mapBtn => {
      this.mapBtn = mapBtn;
    };

    return (
      <>
        <QueryRoute query={{ pwaOverlay: 'map' }}>
          <Layer id="tools-map">
            <Viewport>
              <ViewSmall>
                <SearchResultsMap onDismiss={this.onMapDialogClosed} preloadedTravelAdEnabled={false} dialogView={dialogView} />
              </ViewSmall>
              <ViewMedium>
                <SearchResultsMap onDismiss={this.onMapDialogClosed} preloadedTravelAdEnabled={true} dialogView={dialogView} />
              </ViewMedium>
            </Viewport>
          </Layer>
        </QueryRoute>
        {mapImage && (
          <UitkCard overflow={true}>
            <UitkFigure ratio={UitkFigureAspectRatioType.R21_9} className="uitk-card-roundcorner-top-right uitk-card-roundcorner-top-left">
              <UitkImage alt="" src={mapImage} />
            </UitkFigure>
            <UitkLink align="center">
              <UitkCardContentSection>
                <section
                  data-stid="mapClickButton"
                  className="uitk-button"
                  onClickCapture={handleMapTrigger(history, location, searchResults, analytics)}
                >
                  <LocalizedText message="hotels.searchResults.openMapDialog.label" />
                </section>
              </UitkCardContentSection>
            </UitkLink>
            <UitkCardLink>
              <LinkTracked
                role="button"
                linkName="Launch dynamic map"
                rfrr="Module.MapOpen.Link"
                to={linkTo}
                innerRef={triggerRef}
                onClick={forceFetchProperties(searchResults)}
              >
                <LocalizedText message="hotels.searchResults.openMapDialog.label" />
              </LinkTracked>
            </UitkCardLink>
          </UitkCard>
        )}
        {!mapImage && (
          <ButtonTracked
            data-stid="open-dynamic-map"
            disabled={!isActive || isLoading || isError || isEmpty || null}
            linkName="Launch dynamic map"
            rfrr="Module.MapOpen.Link"
            icon="map"
            isFullWidth={true}
            {...extraProps}
            innerRef={triggerRef}
            onClick={forceFetchProperties(searchResults)}
          >
            <LocalizedText message="hotels.searchResults.openMapDialog.label" />
          </ButtonTracked>
        )}
      </>
    );
  }

  private onMapDialogClosed(): void {
    const { history, location, multiItem, tracker, viewTracker } = this.props;
    updateSearch({
      history,
      location,
      newParams: {
        pwaOverlay: '',
      },
    });

    changeView(tracker, viewTracker, View.LISTINGS, multiItem?.getPackageType());

    if (this.mapBtn && typeof this.mapBtn.focus === 'function') {
      this.mapBtn.focus();
    }
  }
}
